import { SectionTabs } from '../components/SectionTabs';
import type { Section } from '../components/Shell';
import { useAuth } from '../auth/AuthContext';
import { Overview } from './Overview';
import { Analytics } from './Analytics';

/**
 * The dashboard, with site reporting behind a tab rather than its own sidebar
 * entry. Both answer "how are we doing" — one about the work, one about the
 * traffic that produces it — and nobody needs them open at once.
 */

export type HomeTab = 'overview' | 'reporting';

export function Home({
  onOpenRedcap,
  tab,
  onTab,
  onSection,
}: {
  tab: HomeTab;
  onTab: (next: HomeTab) => void;
  onSection: (s: Section) => void;
  /** Open the REDCap section on one study's step — the "This week" card. */
  onOpenRedcap?: (study: string, tab: 'completeness') => void;
}) {
  const { can } = useAuth();
  // Each tab is behind its own read: the work (GET /submissions/overview) and
  // the traffic (GET /analytics/). The sidebar shows the Dashboard to a role
  // holding either; this shows only the tabs it holds, and no switch for one.
  const mayOverview = can('submissions:read');
  const mayReporting = can('analytics:read');
  const shown: HomeTab = tab === 'reporting' ? (mayReporting ? 'reporting' : 'overview') : mayOverview ? 'overview' : 'reporting';

  // Handed to whichever view is showing, so the switch sits under that view's
  // own heading rather than floating above it.
  const tabs =
    mayOverview && mayReporting ? (
      <SectionTabs
        value={shown}
        onChange={onTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'reporting', label: 'Site reporting' },
        ]}
      />
    ) : undefined;

  return shown === 'overview' ? (
    <Overview
      onSection={onSection}
      onReporting={() => onTab('reporting')}
      tabs={tabs}
      onOpenRedcap={onOpenRedcap}
    />
  ) : (
    <Analytics tabs={tabs} />
  );
}
